import React from "react";

interface OrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  quantidade_pedido: number;
  valor_liquido: number;
  valor_total: number;
  delivery_type_id: number;
  delivery_type_number: number;
  brand_id: number;
}

interface BrandGroupProps {
  brandName: string;
  products: OrderItem[];
  subtotal: number;
  formatCurrency: (value: number) => string;
  colorScheme?: "primary" | "blue" | "purple" | "green" | "orange" | "amber";
  /** Mostra a quantidade pedida de cada item ao lado do valor (lista de pedido programado). */
  showQuantity?: boolean;
  /** Desconto do cupom por produto (id do produto), mostrado abaixo do item (lista de pedido programado). */
  itemDiscountByProduct?: Record<number, number>;
}

const BrandGroup = ({ 
  brandName, 
  products, 
  subtotal, 
  formatCurrency, 
  colorScheme = "primary",
  showQuantity = false,
  itemDiscountByProduct
}: BrandGroupProps) => {
  const getColorClasses = () => {
    switch (colorScheme) {
      case "blue":
        return {
          border: "border-blue-500/30",
          text: "text-blue-600",
          bg: "bg-muted/30"
        };
      case "purple":
        return {
          border: "border-amber-500/30", 
          text: "text-amber-600",
          bg: "bg-muted/30"
        };
      case "green":
        return {
          border: "border-green-500/30",
          text: "text-green-600", 
          bg: "bg-muted/30"
        };
      case "orange":
        return {
          border: "border-orange-500/30",
          text: "text-orange-600", 
          bg: "bg-muted/30"
        };
      case "amber":
        return {
          border: "border-amber-500/30",
          text: "text-amber-600",
          bg: "bg-muted/30"
        };
      default:
        return {
          border: "border-primary/30",
          text: "text-primary",
          bg: "bg-muted/30"
        };
    }
  };

  const colors = getColorClasses();

  return (
    <div className="space-y-2">
      <div className={`flex justify-between items-center py-2 px-3 ${colors.bg} rounded-lg border-l-4 ${colors.border}`}>
        <span className="font-medium text-amber-600 text-sm">
          📚 {brandName}
        </span>
        <span className={`font-semibold ${colors.text} text-sm`}>
          {formatCurrency(subtotal)}
        </span>
      </div>
      <div className="ml-4 space-y-1 text-sm text-muted-foreground">
        {products.map((item) => {
          const couponDiscount = itemDiscountByProduct?.[(item as any).product_id ?? item.id] ?? 0;
          return (
          <div
            key={item.id}
            className="flex flex-wrap justify-between py-1 pr-3 border-b border-border/30 last:border-0"
          >
            <span className="truncate flex-1">
              {item.descricao_produto}
            </span>
            {showQuantity && (
              <span className="ml-2 whitespace-nowrap text-xs text-muted-foreground self-center">
                Qtd. {item.quantidade_pedido}
              </span>
            )}
            <span className="ml-2 whitespace-nowrap font-medium">
              {formatCurrency(item.valor_total)}
            </span>
            {couponDiscount > 0 && (
              <span className="basis-full text-right text-xs font-medium text-emerald-700">
                Desconto do cupom: − {formatCurrency(couponDiscount)}
              </span>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default BrandGroup;